import { BrowserModule, Title } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import './_services/error-toast-queue';
import { NgxPaginationModule } from 'ngx-pagination';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';

import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { SignupComponent } from './users/signup/signup.component';
import { AppRoutingModule } from './app-routing.module';
import { SigninComponent } from './users/signin/signin.component';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ContentComponent } from './content/content.component';
import { RegisterBusinessComponent } from './users/register-business/register-business.component';

// used to create fake backend
import { HomeLegacyComponent } from './home-legacy/home-legacy.component';
import { HomeNewComponent } from './home-new/home-new.component';


import { CountryComponent } from './country/country.component';
import { CountryService } from './country/country.service';
import { CommonService } from './_services/common.service';


import { ShoppingCartComponent } from './users/shopping-cart/shopping-cart.component';
import { MyAccountComponent } from './users/my-account/my-account.component';
import { ApiService } from './_services/api.service';
import { ProgressBarComponent } from './header/progress-bar/progress-bar.component';
import { ProgressBarService } from './_services/progress-bar.service';
import { ForgetPasswordComponent } from './users/forget-password/forget-password.component';
import { ResetPasswordComponent } from './users/reset-password/reset-password.component';
import { FpRequrestSentComponent } from './users/fp-requrest-sent/fp-requrest-sent.component';
import { ResetPasswordCompleteComponent } from './users/reset-password-complete/reset-password-complete.component';
import { HeaderWithoutMenuComponent } from './header-without-menu/header-without-menu.component';
import { HashLocationStrategy, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ProductCategoryService } from './_services/product-category.service';
import { ShippingAddressComponent } from './users/shipping-address/shipping-address.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { UserEditFormComponent } from './users/user-edit-form/user-edit-form.component';
import { UserProfilePictureComponent } from './users/user-profile-picture/user-profile-picture.component';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { UserService } from './_services/user.service';
import { AlertService } from './_services/alert.service';
import { AuthenticationService } from './_services/authentication.service';
import { RoleGuard } from './_guards/role.guard';
import { FilterPageGuard } from './_guards/filter-page.guard';
import { AuthGuard } from './_guards/auth.guard';
import { SellerProfileIncompleteGuard } from './_guards/seller-profile-incomplete.guard';
import { SellerProfileCompleteGuard } from './_guards/seller-profile-complete.guard';
import { VerifiedSellerGuard } from './_guards/verified-seller.guard';
import { AlertComponent } from './_directives/alert.component';
import { PageTitleComponent } from './_directives/page-title.component';
import { JwtInterceptor } from './_helpers/jwt.interceptor';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { AdminModule } from './admin/admin.module';
import { Location } from '@angular/common';
import { PageTitleService } from './_services/page-title.service';
import { SliderComponent } from './slider/slider.component';
import { SectorComponent } from './sector/sector.component';
import { SectorService } from './_services/sector.service';
import { BasicDataService } from './_services/basic-data.service';
import { OfferComponent } from './offer/offer.component';
import { AppErrorHandler } from './commons/app-error-handler';
import { OfferFormComponent } from './offer-form/offer-form.component';
import { PricingService } from './_services/pricing.service';
import { ProductsConditionsService } from './_services/products-conditions.service';
import { MeasurementService } from './_services/measurement.service';
import { ProductsConditionsComponent } from './products-conditions/products-conditions.component';
import { MeasurementComponent } from './measurement/measurement.component';
import { OfferViewComponent } from './offer-view/offer-view.component';
import { OfferEditFormComponent } from './user-offer/offer-edit-form/offer-edit-form.component';
import { ProductsListComponent } from './products/products-list/products-list.component';
import { CategoryProductsComponent } from './products/category-products/category-products.component';
import { SingleProductComponent } from './products/single-product/single-product.component';
import { ShippingOfferFormComponent } from './shipping-offer-form/shipping-offer-form.component';
import { NgxsModule } from '@ngxs/store';
import { CartState } from './_cart/cart.state';
import { CartComponent } from './cart/cart.component';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { SearchFormComponent } from './products/search-form/search-form.component';
import { SideNavCategoriesComponent } from './products/side-nav-categories/side-nav-categories.component';
import { SellerProductsComponent } from './products/seller-products/seller-products.component';
import { SideNavCartComponent } from './products/side-nav-cart/side-nav-cart.component';
import { UserChangePasswordComponent } from './users/user-change-password/user-change-password.component';
import { FeaturesBoxComponent } from './features-box/features-box.component';
import { NewsletterComponent } from './newsletter/newsletter.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { PaymentService } from './_services/payment.service';
import { OrderService } from './_services/order.service';
import { WishlistComponent } from './wishlists/wishlist/wishlist.component';
import { WishlistFormComponent } from './wishlists/wishlist-form/wishlist-form.component';
import { WishlistService } from './_services/wishlist.service';
import { BlogComponent } from './blog/blog/blog.component';
import { BlogPostComponent } from './blog/blog-post/blog-post.component';
import { BlogService } from './_services/blog.service';
import { SafeHtmlPipe } from './_pipes/safe-html.pipe';
import { BlogSidebarComponent } from './blog/blog-sidebar/blog-sidebar.component';
import { AddBlogPostComponent } from './blog/add-blog-post/add-blog-post.component';
import { PageService } from './_services/page.service';
import { PageComponent } from './page/page.component';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { QuillModule } from 'ngx-quill';
import { ContactComponent } from './contact/contact.component';
import { TeamComponent } from './team/team.component';
import { ContactService } from './_services/contact.service';
import { PaymentReturnComponent } from './payment-return/payment-return.component';
import { PaymentSuccessComponent } from './payment-success/payment-success.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { OrdersListComponent } from './orders/orders-list/orders-list.component';
import { OrderComponent } from './orders/order/order.component';
import { HomeSliderItemComponent } from './home-legacy/home-slider-item/home-slider-item.component';
import { HomeHeroComponent } from './home-legacy/home-hero/home-hero.component';
import { FarmerAccountComponent } from './users/my-account/farmer-account/farmer-account.component';
import { DocumentService } from './_services/document.service';
import { AdminAccountComponent } from './users/my-account/admin-account/admin-account.component';
import { PaymentEditModalComponent } from './users/my-account/payment-edit-modal/payment-edit-modal.component';
import { VerificationModalComponent } from './users/my-account/verification-modal/verification-modal.component';
import { CompanyInfoEditModalComponent } from './users/my-account/company-info-edit-modal/company-info-edit-modal.component';
import { DocumentPreviewModalComponent } from './users/my-account/document-preview-modal/document-preview-modal.component';
import { DocumentUploadModalComponent } from './users/my-account/document-upload-modal/document-upload-modal.component';
import { PlaceCommandComponent } from './users/place-command/place-command.component';
import { SalesComponent } from './sales/sales.component';
import { PageSidebarComponent } from './page-sidebar/page-sidebar.component';
import { ActivationComponent } from './users/activation/activation.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MobileMenuComponent } from './mobile-menu/mobile-menu.component';
import { SignupConfirmationComponent } from './users/signup-confirmation/signup-confirmation.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { TocurrencyPipe } from './_pipes/tocurrency.pipe';
import { CurrencyState } from './_currency/currency.state';
import { TopbarComponent } from './topbar/topbar.component';
import { HeaderNewComponent } from './header-new/header-new.component';
import { MarketPricesTickerComponent } from './market-prices-ticker/market-prices-ticker.component';
import { WeatherComponent } from './weather/weather.component';
import { ExpertsComponent } from './experts/experts.component';
import { MarketPricesComponent } from './market-prices/market-prices.component';
import { NewSliderComponent } from './new-slider/new-slider.component';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { CategorySliderComponent } from './category-slider/category-slider.component';
import { BestSellingComponent } from './best-selling/best-selling.component';
import { AdContentComponent } from './ad-content/ad-content.component';
import { FeaturedProductsComponent } from './featured-products/featured-products.component';
import { RecommendedProductsComponent } from './recommended-products/recommended-products.component';
import { LatestBlogsComponent } from './latest-blogs/latest-blogs.component';
import { DownloadAppComponent } from './download-app/download-app.component';
import { ServicesComponent } from './services/services.component';
import { RelatedProductsComponent } from './related-products/related-products.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TaxonomyService } from './_services/taxonomy.service';
import { CityService } from './_services/city.service';
import { TaxonomyOffersComponent } from './taxonomy-offers/taxonomy-offers.component';
import { MarketBagComponent } from './market-bag/market-bag.component';
import { BackToTopComponent } from './back-to-top/back-to-top.component';
import { WhatsappChatComponent } from './whatsapp-chat/whatsapp-chat.component';
import { CareerComponent } from './career/career.component';
import { SubscriptionComponent } from './users/subscription/subscription.component';
import { DeliveryRequestsComponent } from './users/delivery-requests/delivery-requests.component';
import { DeliveryRequestDetailComponent } from './users/delivery-requests/delivery-request-detail/delivery-request-detail.component';
import { BillingHistoryComponent } from './users/billing-history/billing-history.component';
import { ShippingMeansComponent } from './users/shipping-means/shipping-means.component';
import { ShippingOptionDetailComponent } from './users/shipping-means/shipping-option-detail/shipping-option-detail.component';
import { SalesListComponent } from './users/sales-list/sales-list.component';
import { SaleDetailComponent } from './users/sale-detail/sale-detail.component';
import { TestimonialsPageComponent } from './testimonials/testimonials-page/testimonials-page.component';
import { PartnersPageComponent } from './partners/partners-page/partners-page.component';
import { DeliveryComponent } from './delivery/delivery.component';

export function migrationState(state) {
    return {
        products: state.products,
        orderId: state.orderId
    };
}

export function eFarmTranslateLoader(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

@NgModule({ declarations: [
        AppComponent,
        HeaderComponent,
        ProgressBarComponent,
        FooterComponent,
        SignupComponent,
        SigninComponent,
        ContentComponent,
        RegisterBusinessComponent,
        HomeLegacyComponent,
        HomeNewComponent,
        ShoppingCartComponent,
        MyAccountComponent,
        CountryComponent,
        AlertComponent,
        PageTitleComponent,
        ForgetPasswordComponent,
        ResetPasswordComponent,
        FpRequrestSentComponent,
        ResetPasswordCompleteComponent,
        HeaderWithoutMenuComponent,
        ShippingAddressComponent,
        SidebarComponent,
        UserEditFormComponent,
        UserProfilePictureComponent,
        SliderComponent,
        SectorComponent,
        OfferComponent,
        OfferFormComponent,
        ProductsConditionsComponent,
        MeasurementComponent,
        OfferViewComponent,
        OfferEditFormComponent,
        ProductsListComponent,
        CategoryProductsComponent,
        SellerProductsComponent,
        SingleProductComponent,
        ShippingOfferFormComponent,
        CartComponent,
        SearchFormComponent,
        SideNavCategoriesComponent,
        SideNavCartComponent,
        UserChangePasswordComponent,
        FeaturesBoxComponent,
        NewsletterComponent,
        CheckoutComponent,
        WishlistComponent,
        WishlistFormComponent,
        BlogComponent,
        BlogPostComponent,
        SafeHtmlPipe,
        BlogSidebarComponent,
        AddBlogPostComponent,
        PageComponent,
        ContactComponent,
        TeamComponent,
        PaymentReturnComponent,
        PaymentSuccessComponent,
        PageNotFoundComponent,
        OrdersListComponent,
        OrderComponent,
        HomeSliderItemComponent,
        HomeHeroComponent,
        FarmerAccountComponent,
        AdminAccountComponent,
        PlaceCommandComponent,
        SalesComponent,
        PageSidebarComponent,
        ActivationComponent,
        MobileMenuComponent,
        SignupConfirmationComponent,
        TocurrencyPipe,
        TopbarComponent,
        MarketPricesTickerComponent,
        WeatherComponent,
        ExpertsComponent,
        MarketPricesComponent,
        HeaderNewComponent,
        NewSliderComponent,
        CategorySliderComponent,
        BestSellingComponent,
        AdContentComponent,
        FeaturedProductsComponent,
        RecommendedProductsComponent,
        LatestBlogsComponent,
        DownloadAppComponent,
        ServicesComponent,
        RelatedProductsComponent,
        TaxonomyOffersComponent,
        MarketBagComponent,
        BackToTopComponent,
        WhatsappChatComponent,
        CareerComponent,
        SubscriptionComponent,
        DeliveryRequestsComponent,
        DeliveryRequestDetailComponent,
         BillingHistoryComponent,
         ShippingMeansComponent,
         ShippingOptionDetailComponent,
         SalesListComponent,
         SaleDetailComponent,
         TestimonialsPageComponent,
        PartnersPageComponent,
        DeliveryComponent,
        PaymentEditModalComponent,
        VerificationModalComponent,
        CompanyInfoEditModalComponent,
        DocumentPreviewModalComponent,
        DocumentUploadModalComponent
    ],
    exports: [
        AlertComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        MatCardModule,
        MatProgressBarModule,
        MatSelectModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        NgxPaginationModule,
        RouterModule,
        ToastrModule.forRoot({
            timeOut: 10000,
            positionClass: 'toast-bottom-left',
            preventDuplicates: true,
            closeButton: true,
            tapToDismiss: true,
            extendedTimeOut: 2000,
        }),
        ImageCropperComponent,
        AdminModule,
        AngularEditorModule,
        QuillModule.forRoot(),
        LazyLoadImageModule,
        SlickCarouselModule,
        NgxSkeletonLoaderModule,
        NgSelectModule,
        NgbModule,
        TranslateModule.forRoot({
            useDefaultLang: true,
            loader: {
                provide: TranslateLoader,
                useFactory: (eFarmTranslateLoader),
                deps: [HttpClient],
            }
        }),
        NgxsModule.forRoot([CartState, CurrencyState], { developmentMode: false }),
        NgxsStoragePluginModule.forRoot({
            keys: '*',
            migrations: [
                {
                    key: 'cart',
                    version: 1,
                    'migrate': migrationState
                }
            ]
        }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production && typeof window !== 'undefined' })], providers: [
        AuthenticationService,
        AlertService,
        UserService,
        AuthGuard,
        RoleGuard,
        FilterPageGuard,
        SellerProfileIncompleteGuard,
        SellerProfileCompleteGuard,
        VerifiedSellerGuard,
        CountryService,
        CommonService,
        Location,
        ProductCategoryService,
        Title,
        ApiService,
        PageTitleService,
        ProgressBarService,
        SectorService,
        BasicDataService,
        PricingService,
        ProductsConditionsService,
        MeasurementService,
        PaymentService,
        OrderService,
        WishlistService,
        BlogService,
        PageService,
        ContactService,
        TaxonomyService,
        CityService,
        DocumentService,
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        { provide: ErrorHandler, useClass: AppErrorHandler },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
}
// {provide: LocationStrategy, useClass: HashLocationStrategy},